import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import OrOptionsDialog from '../../components/Dialogs/OrOptionsDialog'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { WIZARD_INDENTIFIERS } from '../../components/Wizards/constants'
import {
	AppBar,
	Button,
	Container,
	Card,
	CardContent,
	CardHeader,
	Icon,
	makeStyles,
	TextField,
	Toolbar,
	Typography,
	CircularProgress
} from '@material-ui/core'
import {
	// getHouseholds,
	// getHouseholdById,
	getHouseholdByIdAsync,
	getHouseholdsWithAdvancedSearch
} from '../../actions/households/households.actions'
import { postVisit, getVisitsByHouseholdIdAsync } from '../../actions/visits/visits.actions'

import CreateVisitTypeCard from '../../components/Visits/CreateVisitTypeCard'
import CreateVisitPetDetail from '../../components/Pets/CreateVisitPetDetail'
// import VisitInvoiceCard from '../../components/Visits/VisitInvoiceCard'
import { debounce } from 'lodash'
import { enumerateDaysBetweenDates } from '../../services/helpers'
import moment from 'moment'
import clsx from 'clsx'
import {
	FONT_WEIGHTS,
	endDateIsBeforeOrEqualToStartDate,
	VISIT_TYPES,
	ADDON_ONLY,
	GROOMING,
	DAYCARE,
	BOARDING
} from '../../utils'
import GroomingProfileWizard, { ENTRY_POINTS } from '../../components/Wizards/Grooming/GroomingProfileWizard'
import { states } from '../../services/helpers'
import useSelectedHousehold from '../../hooks/useSelectedHousehold'
import useFacilityPaymentStatus from '../../hooks/useFacilityPaymentStatus'
import NoFacilityCardWarning from '../../components/Common/NoFacilityCardWarning'
import DifferentFacilityBookingWarning from '../../components/Common/DifferentFacilityBookingWarning'

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1,
		paddingTop: '24px',
		paddingBottom: '75px'
	},
	userIcon: {
		width: '16px',
		height: '18px',
		'&:before': {
			color: '#777679'
		}
	},
	clearButton: {
		color: '#D32F2F',
		borderColor: '#D32F2F',
		'&:hover': {
			color: '#FFFFFF',
			backgroundColor: '#D32F2F',
			borderColor: '#D32F2F'
		}
	},
	householdSearch: {
		'& .MuiAutocomplete-inputRoot': {
			paddingTop: '8px'
		}
	},
	appBar: {
		top: 'auto',
		bottom: 0
	},
	fetchingClientContainer: {
		display: 'flex',
		alignItems: 'center',
		fontSize: '1.2rem',
		gap: '.6rem',
		fontWeight: FONT_WEIGHTS.Bold
	}
}))

function useQuery() {
	return new URLSearchParams(useLocation().search)
}

const CreateAppointment = () => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const history = useHistory()
	const query = useQuery()
	const facility = useSelector((state) => state.specific.facilities.selected)
	const households = useSelector((state) => state.specific.households.households)
	const { household } = useSelectedHousehold()
	const facilityCardStatus = useFacilityPaymentStatus(household?._id, facility?._id)

	const initialVisit = {
		type: '',
		startDate: null,
		endDate: null,
		datesBetween: [],
		dates: [],
		daysOfTheWeek: [
			{ display: 'M', value: 'monday', selected: false },
			{ display: 'T', value: 'tuesday', selected: false },
			{ display: 'W', value: 'wednesday', selected: false },
			{ display: 'T', value: 'thursday', selected: false },
			{ display: 'F', value: 'friday', selected: false }
		],
		pets: []
	}

	const [open, setOpen] = useState(false)
	const [appointmentMethodDialogOpen, setAppointmentMethodDialogOpen] = useState(false)
	const [search, setSearch] = useState('')
	const [visit, setVisit] = useState({ ...initialVisit })
	const [groomingProfileWizardOpen, setGroomingProfileWizardOpen] = useState(false)
	const [petDetailsForGroomingProfileWizard, setPetDetailsForGroomingProfileWizard] = useState({})
	const [fetching, setFetching] = useState(false)

	const delayedSetSearch = useRef(debounce((t) => setSearch(t), 750)).current

	const loadingInternal = open && households.length === 0

	const isGrooming = useMemo(() => query.get('type') === GROOMING, [query])
	const requestedPetId = useMemo(() => query.get('pet_id'), [query])
	const requestedHouseholdId = useMemo(() => query.get('household'), [query])

	const updateSearch = (value) => delayedSetSearch(value)

	const isAddOnGroomingVisitOnly = visit?.type === ADDON_ONLY && visit?.subtype === GROOMING

	const clearHouseholds = () => {
		dispatch({ type: 'GET_HOUSEHOLDS_SUCCESS', payload: [] })
	}

	const clearHousehold = () => {
		dispatch({ type: 'SET_SELECTED_HOUSEHOLD', payload: null })
	}

	const createVisit = useCallback(() => {
		const newVisit = {
			...visit,
			pet_ids: visit.pets.filter((pet) => pet.selected).map((pet) => pet._id)
		}

		return newVisit
	}, [visit])

	const dialogButtonText = useMemo(() => {
		if (isGrooming) {
			return 'New Grooming Appointment Booking'
		}

		if (visit.type === DAYCARE) {
			return 'New Daycare Appointment Booking'
		}

		if (visit.type === BOARDING) {
			return 'New Boarding Appointment Booking'
		}

		return ''
	}, [isGrooming, visit])

	const goToNewAppointmentView = (visit, wizardId) => {
		clearHouseholds()
		clearHousehold()
		if (!household || !visit || !facility) {
			history.push('/')
		}

		const { _id: visitId } = visit
		const { _id: householdId } = household

		if (!visitId || !householdId) {
			if (isGrooming) {
				history.push(`/facilities/${facility._id}/groomingHome`)
			} else {
				history.push(`/facilities/${facility._id}/schedule`)
			}
		}

		let appointmentUrl = `/clients/${householdId}/appointments/${visitId}`
		if (wizardId && wizardId > '') {
			appointmentUrl += `?wizard=${wizardId}`
		}
		history.push(appointmentUrl)
	}

	const goToManualEntry = useCallback(() => {
		const newVisit = createVisit()
		dispatch(postVisit(newVisit, goToNewAppointmentView))
	}, [dispatch, goToNewAppointmentView, createVisit])

	const handleCloseAppointmentMethodDialog = useCallback(() => {
		setAppointmentMethodDialogOpen(false)
	}, [])

	const handleGrooomingProfileWizardState = useCallback(() => {
		setGroomingProfileWizardOpen((prev) => !prev)
	}, [setGroomingProfileWizardOpen])

	const handleOpenAppointmentMethodDialog = useCallback(() => {
		if (visit) {
			if (visit.type === ADDON_ONLY) {
				goToManualEntry()
				return
			}
			// for all non grooming appointments, we will just go to manual entry for now
			if (!isGrooming && visit.type !== DAYCARE && visit.type !== BOARDING) {
				goToManualEntry()
				return
			}

			if (visit.type === DAYCARE || visit.type === BOARDING) {
				setAppointmentMethodDialogOpen(true)
				return
			}

			if (isGrooming) {
				const { pets } = visit || []
				const selected = pets.find((pet) => pet.selected)
				if (selected && selected.no_grooming_profile === true) {
					setPetDetailsForGroomingProfileWizard(selected)
					setGroomingProfileWizardOpen(true)
				} else {
					setPetDetailsForGroomingProfileWizard({})
					setAppointmentMethodDialogOpen(true)
				}
			}
		}
	}, [visit, goToManualEntry, isGrooming])

	useEffect(() => {
		const fetchHousehold = async () => {
			setFetching(true)
			await getHouseholdByIdAsync(dispatch, requestedHouseholdId)
			setFetching(false)
		}

		if (!household && requestedHouseholdId && requestedHouseholdId > '') {
			fetchHousehold()
		}
	}, [requestedPetId, requestedHouseholdId, household, dispatch, setFetching])

	const setHousehold = useCallback(
		(household) => {
			const fetchHouseholdVisits = async () => {
				await getVisitsByHouseholdIdAsync(dispatch, household._id, {
					excludeInvalidVisits: true,
					includeCurrentMonthForward: true
				})
			}

			dispatch({ type: 'SET_SELECTED_HOUSEHOLD', payload: household })

			if (household) {
				fetchHouseholdVisits()
			}
		},
		[dispatch]
	)

	const goToWizard = useCallback(() => {
		const newVisit = createVisit()
		const { type } = newVisit

		let wizardId = ''

		if (type === GROOMING) {
			wizardId = WIZARD_INDENTIFIERS.GroomingAppointment
		}

		if (type === DAYCARE) {
			wizardId = WIZARD_INDENTIFIERS.DaycareAppointment
			newVisit['status'] = { id: 1, name: 'Pending' }
		}

		if (type === BOARDING) {
			wizardId = WIZARD_INDENTIFIERS.BoardingAppointment
			newVisit['status'] = { id: 1, name: 'Pending' }
		}

		dispatch(postVisit(newVisit, (visit) => goToNewAppointmentView(visit, wizardId)))
	}, [dispatch, createVisit])

	const goToSchedulePage = () => {
		clearHouseholds()
		clearHousehold()
		if (isGrooming) {
			history.push(`/facilities/${facility._id}/groomingHome`)
		} else {
			history.push(`/facilities/${facility._id}/schedule`)
		}
	}

	// const getVisitWithFilteredPets = () => {
	// 	return {
	// 		...visit,
	// 		pets: visit.pets.filter((pet) => pet.selected)
	// 	}
	// }

	const renderAddress = () => {
		let { contact } = household
		let { address } = contact
		let { address_line_1, address_line_2, city, state, zip_code } = address

		const stateId = parseInt(state, 10)
		state = states.find((s) => s.id === stateId)

		return (
			<div>
				<Typography variant="body2">{household.account.full_name || 'No name'}</Typography>
				{contact ? (
					<div>
						<Typography variant="body2">{address_line_1 || 'No address'}</Typography>
						{address_line_2 ? <Typography variant="body2">{address_line_2}</Typography> : null}
						<Typography variant="body2">{`${city ? city : ''}${state ? `, ${state.name}` : ''}${
							zip_code ? ` ${zip_code}` : ''
						}`}</Typography>
					</div>
				) : null}
			</div>
		)
	}

	const dateError = useMemo(() => {
		const { startDate, endDate } = visit
		return endDateIsBeforeOrEqualToStartDate({
			startDate,
			endDate
		})
	}, [visit])

	const visitIsInvalid = useMemo(() => {
		const {
			type,
			subtype = '',
			startDate,
			endDate,
			dates,
			pets,
			time_block,
			standalone_addon_id = null
		} = visit
		if (!type) {
			return true
		}
		if (!startDate) {
			return true
		}
		if (type === BOARDING && !endDate) {
			return true
		}
		if (type === VISIT_TYPES.CompatibilitySession && !time_block) {
			return true
		}
		if (type === ADDON_ONLY && subtype === GROOMING && !standalone_addon_id) {
			return true
		}
		if (dates.length < 1) {
			return true
		}
		if (pets.length < 1) {
			return true
		}
		if (!pets.some((pet) => pet.selected)) {
			return true
		}
		return false
	}, [visit])

	useEffect(() => {
		if ((household && !query.get('household')) || (household && query.get('household') !== household._id)) {
			clearHousehold()
		}
	}, [])

	useEffect(() => {
		if (!facility) {
			history.push('/')
		}
		if (household) {
			setVisit({
				...visit,
				type: isGrooming ? GROOMING : '',
				household_id: household._id,
				facility_id: facility._id,
				pets: household.pets.map((pet) => {
					const { ear_cleaning } = pet
					const no_grooming_profile = !ear_cleaning || ear_cleaning === ''
					return {
						...pet,
						add_on_services: [],
						selected: requestedPetId && requestedPetId > '' ? pet._id === requestedPetId : false,
						no_grooming_profile
					}
				})
			})
		}

		return () => {
			setVisit({ ...initialVisit })
		}
	}, [household, facility, isGrooming, requestedPetId])

	useEffect(() => {
		let { startDate, endDate, type } = visit
		let dates

		if (
			(type === DAYCARE ||
				type === VISIT_TYPES.CompatibilitySession ||
				type === GROOMING ||
				type === ADDON_ONLY) &&
			startDate
		) {
			dates = [moment(startDate).format('YYYY-MM-DD')]
		} else if (type === BOARDING && startDate && endDate) {
			dates = enumerateDaysBetweenDates(startDate, endDate)
		}

		if (visit.dates && dates && JSON.stringify(visit.dates) !== JSON.stringify(dates)) {
			setVisit({
				...visit,
				datesBetween: dates,
				dates
			})
		}
	}, [visit])

	useEffect(() => {
		const doSearch = async () => {
			await getHouseholdsWithAdvancedSearch(dispatch, {
				marketId: facility.market_id,
				search: encodeURIComponent(search),
				searchBy: 'contact',
				leanPayload: true,
				includeMemberships: true,
				includeAccounts: true,
				includePets: true,
				includeGroomingCredits: true
			})
		}
		if (search && facility) {
			doSearch()
			// dispatch(getHouseholds(facility.market_id, encodeURIComponent(search), encodeURIComponent('Contact')))
		} else {
			clearHouseholds()
		}
	}, [search, dispatch, facility])

	useEffect(() => {
		if (!open) {
			clearHouseholds()
			setSearch('')
		}
	}, [open])

	const needsWelcome = useMemo(() => {
		if (visit?.type !== 'daycare') return false
		if (
			visit.pets.find(
				(p) =>
					p.selected &&
					p.temperament_test_status !== 'Passed' &&
					p.temperament_test_status !== 'Scheduled'
			)
		)
			return true
		return false
	}, [visit])

	return (
		<div>
			<Container className={classes.root}>
				{isGrooming ? (
					<GroomingProfileWizard
						open={groomingProfileWizardOpen}
						onClose={handleGrooomingProfileWizardState}
						petDetails={petDetailsForGroomingProfileWizard}
						from={ENTRY_POINTS.create_appointment}
					/>
				) : null}
				{!isAddOnGroomingVisitOnly || isGrooming || visit.type === DAYCARE || visit.type === BOARDING ? (
					<OrOptionsDialog
						open={appointmentMethodDialogOpen}
						onClose={handleCloseAppointmentMethodDialog}
						title={dialogButtonText}
						mainOptionLabel="Open Booking Wizard"
						onMainOptionSelected={goToWizard}
						mainOptionFAIcon="bring-forward"
						showSecondaryOption={needsWelcome ? false : true}
						secondaryOptionLabel="Book Manually"
						onSecondaryOptionSelected={goToManualEntry}
					/>
				) : null}
				{isGrooming && (
					<Typography style={{ fontSize: 22, fontWeight: '800' }} variant="body1">
						New Grooming Appointment
					</Typography>
				)}
				<Card style={{ marginTop: '24px', marginBottom: '24px' }}>
					<CardHeader
						action={
							household ? (
								<Button
									className={classes.clearButton}
									variant="outlined"
									onClick={() => clearHousehold()}>
									Change Client
								</Button>
							) : null
						}
						title="Client Information"
					/>
					<CardContent>
						{!household && !requestedHouseholdId ? (
							<Autocomplete
								id="household-select"
								getOptionLabel={(option) =>
									typeof option === 'string'
										? option?.account?.full_name
										: option.account.full_name || 'No Name'
								}
								filterOptions={(x) => x}
								options={
									households && households.length ? households.filter((h) => h.account) : []
								}
								className={classes.householdSearch}
								autoComplete
								loading={loadingInternal}
								includeInputInList
								disableOpenOnFocus
								open={open}
								onOpen={() => {
									setOpen(true)
								}}
								onClose={() => {
									setOpen(false)
								}}
								onChange={(event, newValue) => {
									setHousehold(newValue)
								}}
								renderOption={(option) => (
									<React.Fragment>
										<div style={{ display: 'flex', flexDirection: 'column' }}>
											<Typography variant="body2">{option?.account?.full_name}</Typography>
											<Typography style={{ fontSize: '10px' }}>
												{option?.account?.email}
											</Typography>
										</div>
									</React.Fragment>
								)}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Search existing clients"
										variant="filled"
										fullWidth
										onChange={(e) => updateSearch(e.target.value)}
									/>
								)}
							/>
						) : null}
						{!household && requestedHouseholdId && fetching ? (
							<div className={classes.fetchingClientContainer}>
								<CircularProgress size={20} />
								Hold tight. We're getting the details for that household.
							</div>
						) : null}
						{household ? (
							<div>
								<div style={{ display: 'flex', alignItems: 'flex-start' }}>
									<Icon
										className={clsx([classes.userIcon, 'far fa-user'])}
										style={{
											fontSize: '16px',
											padding: '2px',
											marginTop: '3px',
											marginRight: '24px'
										}}
									/>
									<div>{household.account ? renderAddress() : null}</div>
								</div>
							</div>
						) : null}
					</CardContent>
				</Card>
				{household ? (
					<div>
						<DifferentFacilityBookingWarning
							facility={facility}
							defaultFacilityId={household.default_facility_id}
							style={{ marginBottom: 16 }}
						/>
						<NoFacilityCardWarning
							facilityName={facilityCardStatus.facilityName || facility?.name}
							hasPaymentMethod={facilityCardStatus.hasPaymentMethod}
							loading={facilityCardStatus.loading}
							style={{ marginBottom: 16 }}
						/>
						<CreateVisitTypeCard visit={visit} setVisit={setVisit} />
						{visit.pets
							.filter((pet) => pet.active && pet?.animal_type?.toLowerCase() === 'dog')
							.map((pet) => (
								<CreateVisitPetDetail key={pet._id} visit={visit} pet={pet} setVisit={setVisit} />
							))}
					</div>
				) : null}
			</Container>
			<AppBar position="fixed" color="primary" className={classes.appBar}>
				<Toolbar>
					<Container>
						<Button
							variant="contained"
							color="secondary"
							disabled={visitIsInvalid || dateError}
							style={{ marginRight: '8px' }}
							onClick={handleOpenAppointmentMethodDialog}>
							Create Appointment
						</Button>
						<Button variant="outlined" color="secondary" onClick={goToSchedulePage}>
							Cancel
						</Button>
					</Container>
				</Toolbar>
			</AppBar>
		</div>
	)
}

export default CreateAppointment
